import { useState, type ReactNode } from "react"
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router"
import {
  ArrowLeft,
  Award,
  Box,
  Check,
  Clock,
  FileText,
  Play,
  Target,
  Users,
  UsersRound,
} from "lucide-react"
import {
  einheitDefinition,
  iddProduktMinuten,
  type IddModulInhalt,
  type IddModulStand,
} from "@nl/shared"

import { formatDatum, formatMinuten } from "@/components/idd/idd-format"
import {
  KapitelAuswahl,
  LaufenderStand,
  ZusammenstellenLink,
  useStarten,
} from "@/components/idd/idd-modul-aktionen"
import {
  IDD_ART_TITEL,
  iddDauerLabel,
  iddInhaltVon,
  iddModul,
  iddStandVon,
  type IddModul,
} from "@/components/idd/idd-module"
import { Button } from "@/components/ui/button"
import { versionedImageUrl } from "@/lib/image-version"
import { useIddInhalte, useIddUebersicht } from "@/lib/idd-queries"
import { useSessionContext } from "@/lib/session"

function Kasten({
  icon,
  titel,
  children,
}: {
  icon: ReactNode
  titel: string
  children: ReactNode
}) {
  return (
    <section className="space-y-3 rounded-md bg-card p-5 ring-1 ring-foreground/10">
      <h2 className="flex items-center gap-2.5 text-[14px] font-semibold">
        <span className="text-primary" aria-hidden>
          {icon}
        </span>
        {titel}
      </h2>
      {children}
    </section>
  )
}

function Haken({ eintraege }: { eintraege: string[] }) {
  return (
    <ul className="space-y-2">
      {eintraege.map((e) => (
        <li key={e} className="flex gap-2.5 text-[13px] leading-snug text-muted-foreground">
          <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-success" strokeWidth={3} aria-hidden />
          {e}
        </li>
      ))}
    </ul>
  )
}

function Hauptaktion({
  modul,
  stand,
  kapitel,
}: {
  modul: IddModul
  stand?: IddModulStand
  kapitel: string[]
}) {
  const navigate = useNavigate()
  const { user } = useSessionContext()
  const { starten, isPending } = useStarten()

  const starte = () =>
    void (modul.art === "gespraech"
      ? starten({
          art: "gespraech",
          modulKey: modul.key,
          durchlauf: {
            personaId: modul.szenario.vorlagePersonaId,
            units: [modul.szenario.einheit],
            mode: "text",
            coachStyleId: user?.coachStyleId ?? undefined,
          },
        })
      : starten({ art: "produkt", modulKey: modul.key, kapitel }))
  const kapitelFehlen =
    modul.art === "produkt" && kapitel.length < modul.training.mindestKapitel

  if (stand?.laufendesTrainingId) {
    const id = stand.laufendesTrainingId
    const runId = stand.laufenderDurchlaufId
    return (
      <div className="flex flex-wrap items-center gap-2">
        <Button
          onClick={() =>
            void (runId
              ? navigate({ to: "/durchlauf/$runId", params: { runId } })
              : navigate({ to: "/idd/training/$id", params: { id } }))
          }
        >
          <Play className="h-4 w-4" aria-hidden />
          {stand.laufendStatus === "pruefung" ? "Zur Lernerfolgskontrolle" : "Fortsetzen"}
        </Button>
        {modul.art === "gespraech" && (
          <ZusammenstellenLink
            phase={einheitDefinition(modul.szenario.einheit).phasen[0]}
            laufendesTrainingId={id}
          />
        )}
      </div>
    )
  }

  if (stand?.angerechnetAm && stand.nachweisId) {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <Button asChild>
          <Link to="/idd/nachweis/$id" params={{ id: stand.nachweisId }} search={{ drucken: true }}>
            <Award className="h-4 w-4" aria-hidden />
            Zertifikat herunterladen
          </Link>
        </Button>
        <Button
          variant="ghost"
          onClick={starte}
          disabled={isPending || kapitelFehlen}
          title={kapitelFehlen ? "Wähle zuerst die Lernbausteine." : undefined}
        >
          Erneut üben, ohne Anrechnung
        </Button>
      </div>
    )
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Button onClick={starte} disabled={isPending || kapitelFehlen}>
        <Play className="h-4 w-4" aria-hidden />
        Training starten
      </Button>
      {modul.art === "gespraech" && (
        <ZusammenstellenLink phase={einheitDefinition(modul.szenario.einheit).phasen[0]} />
      )}
      {kapitelFehlen && (
        <span className="text-[12.5px] text-muted-foreground">
          Wähle unten mindestens {modul.training.mindestKapitel} Lernbausteine.
        </span>
      )}
    </div>
  )
}

function IddModulSeite({
  modul,
  inhalt,
  stand,
}: {
  modul: IddModul
  inhalt?: IddModulInhalt
  stand?: IddModulStand
}) {
  const [kapitel, setKapitel] = useState<string[]>([])
  const laeuft = Boolean(stand?.laufendesTrainingId)
  const minuten =
    modul.art === "produkt" && kapitel.length > 0 && !laeuft
      ? formatMinuten(iddProduktMinuten(modul.training, kapitel))
      : iddDauerLabel(modul).replace("Min.", "Minuten")
  const ArtIcon = modul.art === "gespraech" ? Users : Box

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <Button asChild variant="ghost" size="sm" className="-ml-2">
        <Link to="/idd">
          <ArrowLeft className="h-4 w-4" aria-hidden />
          Zurück
        </Link>
      </Button>

      <div className="grid gap-6 md:grid-cols-[minmax(0,360px)_minmax(0,1fr)] md:items-center">
        <div className="relative aspect-[16/10] overflow-hidden rounded-md bg-muted">
          {inhalt && (
            <img
              src={versionedImageUrl(inhalt.bild)}
              alt=""
              className="absolute inset-0 h-full w-full object-cover object-[center_18%]"
            />
          )}
          <span className="idd-dauer">
            <Clock className="h-3.5 w-3.5" aria-hidden />
            {iddDauerLabel(modul)}
          </span>
        </div>
        <div className="space-y-3">
          <p className="flex items-center gap-2 text-[13px] font-semibold text-primary">
            <ArtIcon className="h-4 w-4" aria-hidden />
            {IDD_ART_TITEL[modul.art]}
          </p>
          <h1 className="font-display text-[28px] leading-tight font-medium tracking-tight">
            {modul.titel}
          </h1>
          {inhalt && (
            <p className="max-w-2xl text-[14px] leading-relaxed text-muted-foreground">
              {inhalt.beschreibung}
            </p>
          )}
          {stand?.angerechnetAm && (
            <p className="flex items-center gap-2 text-[13px] text-success">
              <Check className="h-4 w-4" strokeWidth={3} aria-hidden />
              Absolviert am {formatDatum(stand.angerechnetAm)}
            </p>
          )}
          <div className="pt-1">
            <Hauptaktion modul={modul} stand={stand} kapitel={kapitel} />
          </div>
        </div>
      </div>

      {modul.art === "produkt" && (
        <section className="rounded-md bg-card p-5 ring-1 ring-foreground/10">
          {laeuft && stand?.laufendesTrainingId && stand.laufendeKapitel ? (
            <LaufenderStand
              key={stand.laufendeKapitel.map((k) => k.key).join()}
              trainingId={stand.laufendesTrainingId}
              training={modul.training}
              kapitel={stand.laufendeKapitel}
              pruefung={stand.laufendStatus === "pruefung"}
            />
          ) : (
            <KapitelAuswahl
              training={modul.training}
              gewaehlt={kapitel}
              onUmschalten={(key) =>
                setKapitel((alt) =>
                  alt.includes(key) ? alt.filter((k) => k !== key) : [...alt, key]
                )
              }
            />
          )}
        </section>
      )}

      {inhalt && (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-[1fr_1fr_1fr_minmax(0,0.8fr)]">
            <Kasten icon={<Target className="h-4 w-4" />} titel="Lernziele">
              <Haken eintraege={inhalt.lernziele} />
            </Kasten>
            <Kasten icon={<FileText className="h-4 w-4" />} titel="Lerninhalte">
              <Haken eintraege={inhalt.lerninhalte} />
            </Kasten>
            <Kasten icon={<Award className="h-4 w-4" />} titel="Erworbene Kompetenzen">
              <Haken eintraege={inhalt.kompetenzen} />
            </Kasten>
            <Kasten icon={<Clock className="h-4 w-4" />} titel="IDD">
              <p className="font-display text-[26px] leading-none font-medium tabular-nums">
                {minuten}
              </p>
              <p className="text-[13px] leading-snug text-muted-foreground">
                Diese Einheit ist als IDD-Weiterbildungszeit anrechenbar.
              </p>
            </Kasten>
          </div>
          {inhalt.zielgruppe && (
            <section className="flex flex-wrap items-center gap-x-5 gap-y-1.5 rounded-md bg-card px-5 py-4 ring-1 ring-foreground/10">
              <h2 className="flex items-center gap-2.5 text-[14px] font-semibold">
                <UsersRound className="h-4 w-4 text-primary" aria-hidden />
                Zielgruppe
              </h2>
              <p className="text-[13px] text-muted-foreground">{inhalt.zielgruppe}</p>
            </section>
          )}
        </>
      )}
    </div>
  )
}

function ModulRoute() {
  const { art, key } = Route.useParams()
  const modul = iddModul(art, key)
  const { data: uebersicht } = useIddUebersicht()
  const { data: inhalte } = useIddInhalte()
  if (!modul) {
    return (
      <div className="mx-auto max-w-6xl space-y-3">
        <p className="text-[14px]">Dieses Training gibt es im IDD-Bereich nicht.</p>
        <Button asChild variant="outline" size="sm">
          <Link to="/idd">Zum IDD-Bereich</Link>
        </Button>
      </div>
    )
  }
  return (
    <IddModulSeite
      key={`${modul.art}-${modul.key}`}
      modul={modul}
      inhalt={iddInhaltVon(inhalte, modul)}
      stand={iddStandVon(uebersicht?.module, modul)}
    />
  )
}

export const Route = createFileRoute("/_app/idd/modul/$art/$key")({
  component: ModulRoute,
})
